'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import { DefaultValueConfig, isUnlimitedLoyaltyLocale } from '@headless-workspace/config';
import {
    DynamicTagValue,
    HeaderConnectionBannerItemId,
    HeaderConnectionBannerItemValue,
    HeaderSlotValue,
} from '@headless-workspace/core-domain';
import { UIHoverBanner } from '@headless-workspace/core-ui';
import { getIcon, GlowDivider, DividerDirection, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    AppointmentConnectionBannerItem,
    ConnectionBannerGreeting,
    ConnectionBannerItem,
    ConnectionBannerItemWrapper,
    ConnectionBannerLogoutButton,
    ConnectionDataLayerLabel,
    ConnectionHoverButton,
    getConnectedUserInfo,
    HighlightedOffer,
    isUserGoldOrUnlimited,
    OfferToastBannerConnection,
    useUserInfo,
} from '../../../../../src';

type ConnectionBannerProps = PropsWithLocale & {
    hoverButton: HeaderSlotValue;
    bannerItems: HeaderConnectionBannerItemValue[];
    isEnableOffers?: boolean;
    dynamicOfferRules?: DynamicTagValue;
    hostUrl: string;
};

const getConnectionBannerItemFromId = (
    bannerItem: HeaderConnectionBannerItemValue,
    highlightedOffer?: HighlightedOffer,
) => {
    switch (bannerItem.id) {
        case HeaderConnectionBannerItemId.MyOffers:
            return (
                <div key={bannerItem.id} className={'flex flex-col row-span-2 gap-0.5'}>
                    <ConnectionBannerItemWrapper
                        key={bannerItem.id}
                        connectionDataLayer={ConnectionDataLayerLabel[bannerItem.id]}
                        isRedDot={!!highlightedOffer}
                        {...bannerItem}
                    />
                    {highlightedOffer && highlightedOffer.title && (
                        <OfferToastBannerConnection
                            title={highlightedOffer.title}
                            promoCode={highlightedOffer.promoCode}
                        />
                    )}
                </div>
            );

        case HeaderConnectionBannerItemId.FollowMyOrder:
            return (
                <ConnectionBannerItemWrapper
                    key={bannerItem.id}
                    connectionDataLayer={ConnectionDataLayerLabel[bannerItem.id]}
                    {...bannerItem}
                />
            );

        case HeaderConnectionBannerItemId.MyLoyaltyProgram:
            return (
                <ConnectionBannerItemWrapper
                    key={bannerItem.id}
                    connectionDataLayer={ConnectionDataLayerLabel[bannerItem.id]}
                    {...bannerItem}
                />
            );
        case HeaderConnectionBannerItemId.MyAccount:
        case HeaderConnectionBannerItemId.MyOrders:
        case HeaderConnectionBannerItemId.MySettings:
        case HeaderConnectionBannerItemId.MyMessages:
        case HeaderConnectionBannerItemId.Help:
        case HeaderConnectionBannerItemId.MyNews:
            return (
                <ConnectionBannerItem
                    key={bannerItem.id}
                    Icon={getIcon(bannerItem.iconName)}
                    href={bannerItem.href ?? DefaultValueConfig.href}
                    dataLayer={{
                        label: ConnectionDataLayerLabel[bannerItem.id],
                    }}
                    {...bannerItem}
                />
            );
        case HeaderConnectionBannerItemId.MakeAnAppointment:
            return <AppointmentConnectionBannerItem key={bannerItem.id} {...bannerItem} />;

        default:
            return null;
    }
};

export const ConnectionBanner: Component<ConnectionBannerProps> = ({
    locale,
    hoverButton,
    isEnableOffers,
    bannerItems,
    hostUrl,
}) => {
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const pathname = usePathname();
    const { result: userInfoResult, isLoading } = useUserInfo();

    const userInfo = getConnectedUserInfo(userInfoResult);
    const isUnlimitedGold = userInfo && isUnlimitedLoyaltyLocale(locale) && isUserGoldOrUnlimited(userInfo);

    const accessToHighlightedOffer = isEnableOffers && isUnlimitedGold;

    const onLogout = (state: boolean) => {
        setIsLoggingOut(state);
    };

    useEffect(() => {
        setIsLoggingOut(false);
    }, [pathname]);

    return (
        <UIHoverBanner
            className={clsx('flex-row justify-start gap-2.5', 'w-fullViewport desktop:w-maxWidth', 'fill-text-primary')}
            hoverItem={<ConnectionHoverButton isLoggingOut={isLoggingOut} locale={locale} {...hoverButton} />}
        >
            <ConnectionBannerGreeting
                isLoggingOut={isLoggingOut}
                userInfo={userInfo}
                isLoading={isLoading}
                locale={locale}
            />
            <GlowDivider className={'gap-1'} direction={DividerDirection.Vertical} />
            <div className={'grid grid-cols-2 gap-x-2.5 gap-y-1 auto-rows-max'}>
                {bannerItems.map((bannerItem) => (
                    <React.Fragment key={bannerItem.id}>
                        {getConnectionBannerItemFromId(
                            bannerItem,
                            accessToHighlightedOffer ? userInfo?.highlightedOffer : undefined,
                        )}
                    </React.Fragment>
                ))}
                <ConnectionBannerLogoutButton hostUrl={hostUrl} isLoggingOut={isLoggingOut} onLogout={onLogout} />
            </div>
        </UIHoverBanner>
    );
};
